import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import axios from "axios";
import { deltamathAPI } from "../utils";

interface TeacherCodeForm {
  teacherCode: string;
  sectionId: string;
}

interface ClassItem {
  userId: string;
  sourcedId: string;
  legacyId: string;
  name: string;
  email: string;
  roles: string;
  status: string;
}

export const RosterAudit: React.FC = () => {
  const [submittedData, setSubmittedData] = useState<{
    teacherCode: string;
    sectionId: string;
  } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TeacherCodeForm>();

  const auditRosterMutation = useMutation({
    mutationFn: (body: string) => {
      return axios.post(`${deltamathAPI()}/lti/1_3/audit-roster-sync`, body, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
  });

  const onSubmit = (data: TeacherCodeForm) => {
    const body = {
      teacherCode: data.teacherCode,
      sectionId: data.sectionId,
    };

    setSubmittedData(data);
    auditRosterMutation.mutate(JSON.stringify(body));
  };

  return (
    <div className="mx-auto max-w-4xl p-6">
      <h1 className="mb-6 text-2xl font-bold">LTI 1.3 Roster Audit</h1>

      {/* Teacher Code and Section ID Form */}
      <div className="mb-6 rounded-lg bg-white p-6 shadow-md">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor="teacherCode"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Teacher Code
              </label>
              <input
                type="text"
                id="teacherCode"
                className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter teacher code"
              />
              {errors.teacherCode && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.teacherCode.message}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="sectionId"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Section ID
              </label>
              <input
                type="text"
                id="sectionId"
                {...register("sectionId", {
                  required: "Section ID is required",
                  minLength: {
                    value: 1,
                    message: "Section ID cannot be empty",
                  },
                })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter section ID"
              />
              {errors.sectionId && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.sectionId.message}
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              className="rounded-md bg-blue-600 px-6 py-2 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              Search
            </button>
          </div>
        </form>
      </div>

      {/* Loading State */}
      {auditRosterMutation.isPending && (
        <div className="rounded-lg bg-white p-6 shadow-md">
          <div className="flex items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading roster data...</span>
          </div>
        </div>
      )}

      {/* Error State */}
      {auditRosterMutation.isError && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error loading roster data
              </h3>
              <div className="mt-2 text-sm text-red-700">
                {auditRosterMutation.error instanceof Error
                  ? auditRosterMutation.error.message
                  : "An unexpected error occurred"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Class Items List */}
      {auditRosterMutation.isSuccess &&
        auditRosterMutation.data?.data &&
        Array.isArray(auditRosterMutation.data.data) &&
        auditRosterMutation.data.data.length > 0 && (
          <div className="rounded-lg bg-white p-6 shadow-md">
            <h2 className="mb-4 text-lg font-semibold">
              Roster Items for Teacher Code: {submittedData?.teacherCode},
              Section ID: {submittedData?.sectionId}
            </h2>
            <div className="space-y-3">
              {auditRosterMutation.data.data.map(
                (classItem: ClassItem, i: number) => (
                  <div
                    key={`classItem${i}`}
                    className="flex items-center justify-between rounded-lg border border-gray-200 p-4 hover:bg-gray-50"
                  >
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">
                        {classItem.name}
                      </h3>
                      <div className="mt-1 text-sm text-gray-500">
                        <p>Email: {classItem.email}</p>
                        <p>User ID: {classItem.userId}</p>
                        <p>Sourced ID: {classItem.sourcedId}</p>
                        <p>Legacy ID: {classItem.legacyId}</p>
                        <p>Roles: {classItem.roles}</p>
                        <p>Status: {classItem.status}</p>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        )}

      {/* No Class Items Found */}
      {auditRosterMutation.isSuccess &&
        auditRosterMutation.data?.data &&
        Array.isArray(auditRosterMutation.data.data) &&
        auditRosterMutation.data.data.length === 0 && (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  No roster items found
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  No roster items were found for teacher code{" "}
                  {submittedData?.teacherCode} and section ID{" "}
                  {submittedData?.sectionId}.
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};
